import {
  DashboardFilterParams,
  DashboardMerchantsSummaryParams,
  DashboardPeriodParams,
  DashboardProvidersSummaryParams,
  DashboardTransactionsSummaryParams,
} from 'config/requests/dashboard';

import { useGetDashboardMerchantsSummary } from './useGetDashboardMerchantsSummary';
import { useGetDashboardProvidersSummary } from './useGetDashboardProvidersSummary';
import { useGetDashboardTransactionsDynamicSummary } from './useGetDashboardTransactionsDynamicSummary';
import { useGetDashboardTransactionsSuccessSummary } from './useGetDashboardTransactionsSuccessSummary';
import { useGetDashboardTransactionsSummary } from './useGetDashboardTransactionsSummary';
import { DashboardFilterValues } from '../DashboardFilter/hooks/useDashboardFilters';

const toPeriodParams = (values: DashboardFilterValues): DashboardPeriodParams => ({
  periodFrom: values.periodFrom,
  periodTo: values.periodTo,
  timezone: values.timezone,
});

const toFiltersParams = (values: DashboardFilterValues): DashboardFilterParams => ({
  merchant: values.merchant,
  provider: values.provider,
  gateway: values.gateway,
});

const toEntitiesSummaryParams = (
  values: DashboardFilterValues,
): DashboardMerchantsSummaryParams & DashboardProvidersSummaryParams => ({
  ...toPeriodParams(values),
  ...toFiltersParams(values),
});
const toSuccessSummaryParams = toEntitiesSummaryParams;

const toTransactionsSummaryParams = (
  values: DashboardFilterValues,
): DashboardTransactionsSummaryParams => ({
  ...toSuccessSummaryParams(values),
  currency: values.currency,
});

export function useDashboardData(values: DashboardFilterValues | null) {
  const entitiesSummaryParams = values ? toEntitiesSummaryParams(values) : null;
  const transactionsSummaryParams = values ? toTransactionsSummaryParams(values) : null;

  const dashboardTransactionsSummary =
    useGetDashboardTransactionsSummary(transactionsSummaryParams);
  const dashboardTransactionsDynamicSummary =
    useGetDashboardTransactionsDynamicSummary(transactionsSummaryParams);
  const dashboardTransactionsSuccessSummary =
    useGetDashboardTransactionsSuccessSummary(entitiesSummaryParams);
  const dashboardMerchantsSummary = useGetDashboardMerchantsSummary(entitiesSummaryParams);
  const dashboardProvidersSummary = useGetDashboardProvidersSummary(entitiesSummaryParams);

  const summary = dashboardTransactionsSuccessSummary.summary;

  const queries = [
    dashboardTransactionsSummary,
    dashboardTransactionsDynamicSummary,
    dashboardTransactionsSuccessSummary,
    dashboardMerchantsSummary,
    dashboardProvidersSummary,
  ];

  const isLoading = queries.some((query) => query.isLoading);
  const hasErrors = queries.some((query) => Boolean(query.error));

  return {
    dashboardTransactionsSummary,
    dashboardTransactionsDynamicSummary,
    dashboardTransactionsSuccessSummary,
    dashboardMerchantsSummary,
    dashboardProvidersSummary,
    isSummaryAvailable: summary !== undefined,
    hasResults: Number(summary?.total) > 0,
    hasErrors,
    isLoading,
  };
}
